import { useMemo } from "react";
import { Link, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useRegistrationJourney } from "journey";
import { GTag, useTermsAndConditions } from "hooks";
import {
  FormCheckbox,
  RegistrationJourneyButtons,
  RegistrationStepPage,
  DashedDivider,
} from "components";
import { TermsAndConditionSkeleton } from "./TermsAndConditionSkeleton";
import { TermsHtmlContainer } from "./TermsHtmlContainer";

const getValidationSchema = (t: TFunction) =>
  yup.object({
    termsAccepted: yup
      .boolean()
      .required(t("terms.acceptError"))
      .oneOf([true], t("terms.acceptError")),
    termsPhotoAccepted: yup
      .boolean()
      .required(t("terms.acceptPhotoError"))
      .when("$photoRequired", ([photoRequired], schema) =>
        photoRequired ? schema.isTrue(t("terms.acceptPhotoError")) : schema
      ),
  });

type TermsFormData = yup.InferType<ReturnType<typeof getValidationSchema>>;

export const TermsAndCondition = () => {
  const { t } = useTranslation();
  const { selectedExam, termsAccepts, acceptTerms } = useRegistrationJourney();
  const { isFetching, fileName, rawContent } = useTermsAndConditions(
    selectedExam?.id
  );

  const schema = useMemo(() => getValidationSchema(t), [t]);

  const { handleSubmit, control } = useForm<TermsFormData>({
    resolver: yupResolver(schema),
    defaultValues: termsAccepts ?? {
      termsAccepted: false,
      termsPhotoAccepted: false,
    },
    context: { photoRequired: selectedExam?.photoRequired },
  });

  const onSubmit = (data: TermsFormData) => {
    acceptTerms({
      termsAccepted: data.termsAccepted,
      termsPhotoAccepted: data.termsPhotoAccepted,
    });
    GTag.beginCheckout(selectedExam);
  };

  return (
    <RegistrationStepPage stepIndex={1}>
      {isFetching ? (
        <TermsAndConditionSkeleton />
      ) : (
        <TermsHtmlContainer html={rawContent} />
      )}

      <Stack sx={{ alignItems: "flex-end", mb: 2 }}>
        <Link
          href={`https://bccdn.britishcouncil.org/apps/entrynet/regulations/${fileName}`}
          target="_blank"
        >
          {t("terms.printableVersion")}
        </Link>
      </Stack>
      <DashedDivider />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack sx={{ gap: 2, py: 2 }}>
          <FormCheckbox
            name="termsAccepted"
            label={t("terms.acceptLabel")}
            control={control}
            size="small"
            bolded
          />
          {selectedExam?.photoRequired && (
            <FormCheckbox
              name="termsPhotoAccepted"
              label={t("terms.acceptPhotoLabel")}
              control={control}
              size="small"
              bolded
            />
          )}
        </Stack>
        <DashedDivider />

        <RegistrationJourneyButtons />
      </form>
    </RegistrationStepPage>
  );
};
